import * as React from 'react';
import { Nav } from 'office-ui-fabric-react/lib/Nav';
import { useSelector, useDispatch } from 'react-redux';
import { push } from 'connected-react-router';

import {
  currentUserIsAdministratorSelector,
  currentUserIsHearingCreatorSelector,
  currentUserIdSelector,
} from '../features/authenticationSlice';
import {
  ADMIN_HOME_URL,
  ADMIN_SUBJECTAREA_URL,
  ADMIN_GLOBAL_CONTENT_URL,
  ADMIN_HEARINGTYPE_URL,
  ADMIN_KLE_URL,
  ADMIN_FIELDTEMPLATES_URL,
  CREATE_HEARING_URL,
  CURRENT_HEARINGS_URL,
  FINALIZED_HEARINGS_URL,
  MY_HEARINGS_ANSWERED_URL,
  MY_HEARINGS_REVIEWER_URL,
  MY_HEARINGS_WROTE_URL,
  MY_HEARINGS_INVITED_URL,
  ADMIN_CITYAREA_URL,
  ADMIN_INVITATIONGROUPS_URL,
} from '../constants/urls';
import { useTranslation } from 'react-i18next';
import { isUserInHearingRoleSelector } from '../features/apiFeatures/userHearingRoleSlice';
import {
  HEARINGROLE_INVITEE,
  HEARINGROLE_OWNER,
  HEARINGROLE_REVIEWER,
} from '../constants/api';

const getSubMenuNumber = (
  pathName,
  myHearingsWroteNavLinkIsShown,
  myHearingsReviewerNavLinkIsShown,
) => {
  const numberToSubtractFromMyHearingSubMenuNumbers =
    (myHearingsWroteNavLinkIsShown ? 0 : 1) +
    (myHearingsReviewerNavLinkIsShown ? 0 : 1);

  switch (pathName) {
    case ADMIN_HOME_URL:
      return 1;
    case ADMIN_SUBJECTAREA_URL:
      return 2;
    case ADMIN_CITYAREA_URL:
      return 3;
    case ADMIN_GLOBAL_CONTENT_URL:
      return 4;
    case ADMIN_HEARINGTYPE_URL:
      return 5;
    case ADMIN_KLE_URL:
      return 6;
    case ADMIN_FIELDTEMPLATES_URL:
      return 7;
    case ADMIN_INVITATIONGROUPS_URL:
      return 8;
    case MY_HEARINGS_WROTE_URL:
    case CREATE_HEARING_URL:
      return 1;
    case MY_HEARINGS_REVIEWER_URL:
      return 2 - numberToSubtractFromMyHearingSubMenuNumbers;
    case MY_HEARINGS_ANSWERED_URL:
      return 3 - numberToSubtractFromMyHearingSubMenuNumbers;
    case MY_HEARINGS_INVITED_URL:
      return 4 - numberToSubtractFromMyHearingSubMenuNumbers;
    default:
      return 1;
  }
};

const navBarStyles = (
  pathname,
  myHearingsWroteNavLinkIsShown,
  myHearingsReviewerNavLinkIsShown,
) => {
  const selectedSubMenu = getSubMenuNumber(
    pathname,
    myHearingsWroteNavLinkIsShown,
    myHearingsReviewerNavLinkIsShown,
  );
  const subMenuSelector =
    'li:nth-child(' + selectedSubMenu + ') .ms-Nav-linkText';

  return {
    root: { width: '100%' },
    backgroundColor: '#F4F4F4',
    navItems: {
      listStyleType: 'none',
      selectors: {
        'li .is-selected .ms-Nav-link:hover': {
          backgroundColor: '#EDEBE9',
        },
      },
    },
    link: {
      paddingLeft: '15px',
      selectors: {
        '.ms-Nav-compositeLink:hover & .ms-button-Icon': {
          color: '#707070',
        },
        '.ms-Nav-compositeLink.is-selected & .ms-button-Icon': {
          color: '#000000',
        },
      },
    },
    compositeLink: {
      selectors: {
        '.ms-Nav-link:after': {
          border: '0',
        },
      },
    },
    navItem: {
      selectors: {
        li: {
          paddingLeft: '23px',
          backgroundColor: '#EDEBE9',
          height: '35px',
          display: 'flex',
          alignItems: 'center',
        },
        'li .ms-Nav-linkText': {
          fontSize: '13px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
        },
        'li .ms-Nav-compositeLink': {
          height: '100%', // TODO: Add more  styling to sub menus here.
          display: 'flex',
          alignItems: 'center',
        },
        'li .ms-Nav-link': {
          height: '100%',
        },
        'li .ms-Nav-link:hover': {
          backgroundColor: '#EDEBE9',
        },
        'li .ms-Nav-link:hover .ms-Nav-linkText': {
          backgroundColor: '#EDEBE9',
          color: '#333',
          fontWeight: '600',
        },
        'li:last-child': {
          paddingBottom: '15px',
        },
        [subMenuSelector]: {
          fontWeight: '600',
          color: 'black',
        },
      },
    },
    chevronButton: {
      display: 'none',
    },
    linkText: {
      paddingLeft: '25px',
      fontSize: '14px',
    },
  };
};

const findSelectedKey = (pathname) => {
  let selectedKey = '';

  switch (pathname) {
    case CURRENT_HEARINGS_URL: {
      selectedKey = 'CurrentHearing';
      break;
    }
    case FINALIZED_HEARINGS_URL: {
      selectedKey = 'FinalizedHearing';
      break;
    }
    case ADMIN_HOME_URL:
    case ADMIN_SUBJECTAREA_URL:
    case ADMIN_CITYAREA_URL:
    case ADMIN_HEARINGTYPE_URL:
    case ADMIN_KLE_URL:
    case ADMIN_FIELDTEMPLATES_URL:
    case ADMIN_INVITATIONGROUPS_URL:
    case ADMIN_GLOBAL_CONTENT_URL: {
      selectedKey = 'Administration';
      break;
    }
    case CREATE_HEARING_URL:
    case MY_HEARINGS_ANSWERED_URL:
    case MY_HEARINGS_WROTE_URL:
    case MY_HEARINGS_REVIEWER_URL:
    case MY_HEARINGS_INVITED_URL: {
      selectedKey = 'MyHearing';
      break;
    }
    default:
      break;
  }
  return selectedKey;
};

const LeftSideMenu = ({ pathname }) => {
  const { t: translator } = useTranslation();
  const dispatch = useDispatch();
  const currentUserId = useSelector(currentUserIdSelector);
  const isAdministrator = useSelector(currentUserIsAdministratorSelector);
  const isHearingCreator = useSelector(currentUserIsHearingCreatorSelector);
  const isHearingReviewer = useSelector(
    isUserInHearingRoleSelector(currentUserId, HEARINGROLE_REVIEWER),
  );
  const isHearingOwner = useSelector(
    isUserInHearingRoleSelector(currentUserId, HEARINGROLE_OWNER),
  );
  const isHearingInvitee = useSelector(
    isUserInHearingRoleSelector(currentUserId, HEARINGROLE_INVITEE),
  );

  const selectedKey = findSelectedKey(pathname);

  const myHearingsWroteNavLinkIsShown = isHearingCreator || isHearingOwner;
  const myHearingsReviewerNavLinkIsShown = isHearingReviewer;

  const onLinkClick = (event, linkInfo) => {
    event.preventDefault();
    dispatch(push(linkInfo.url));
  };

  const onRenderGroupHeader = () => {
    return <p className="left-side-menu-header">Menu</p>;
  };

  return (
    <Nav
      selectedKey={selectedKey}
      styles={navBarStyles(
        pathname,
        myHearingsWroteNavLinkIsShown,
        myHearingsReviewerNavLinkIsShown,
      )}
      ariaLabel="Navigationsmenu"
      onLinkClick={onLinkClick}
      onRenderGroupHeader={onRenderGroupHeader}
      className={'left-side-menu'}
      groups={[
        {
          name: 'Menu',
          iconName: 'Settings',
          links: [
            {
              key: 'CurrentHearing',
              name: translator('pageNames.currentHearings'),
              url: CURRENT_HEARINGS_URL,
              iconProps: {
                iconName: 'NoteForward',
                className: 'left-side-menu-icon',
              },
            },
            {
              key: 'MyHearing',
              name: translator('pageNames.myHearings'),
              url:
                isHearingCreator || isHearingOwner
                  ? MY_HEARINGS_WROTE_URL
                  : MY_HEARINGS_ANSWERED_URL,
              isExpanded: [
                MY_HEARINGS_ANSWERED_URL,
                MY_HEARINGS_WROTE_URL,
                MY_HEARINGS_REVIEWER_URL,
                MY_HEARINGS_INVITED_URL,
                CREATE_HEARING_URL,
              ].includes(pathname),
              iconProps: {
                iconName: 'TemporaryUser',
                className: 'left-side-menu-icon',
              },
              links:
                isHearingCreator ||
                isHearingOwner ||
                isHearingReviewer ||
                isHearingInvitee
                  ? [
                      ...(myHearingsWroteNavLinkIsShown
                        ? [
                            {
                              key: 'created',
                              name: translator('pageNames.myHearingsWritten'),
                              url: MY_HEARINGS_WROTE_URL,
                            },
                          ]
                        : []),
                      ...(myHearingsReviewerNavLinkIsShown
                        ? [
                            {
                              key: 'reviewer',
                              name: translator('pageNames.myHearingsReviewer'),
                              url: MY_HEARINGS_REVIEWER_URL,
                            },
                          ]
                        : []),
                      {
                        key: 'answered',
                        name: translator('pageNames.myHearingsResponded'),
                        url: MY_HEARINGS_ANSWERED_URL,
                      },
                      {
                        key: 'invited',
                        name: translator('pageNames.myHearingsInvited'),
                        url: MY_HEARINGS_INVITED_URL,
                      },
                    ]
                  : null,
            },
            {
              key: 'FinalizedHearing',
              name: translator('pageNames.finalizedHearings'),
              url: FINALIZED_HEARINGS_URL,
              iconProps: {
                iconName: 'FabricFormLibrary',
                className: 'left-side-menu-icon',
              },
            },
            ...(isAdministrator
              ? [
                  {
                    key: 'Administration',
                    name: translator('pageNames.administration'),
                    url: ADMIN_HOME_URL,
                    isExpanded: [
                      ADMIN_HOME_URL,
                      ADMIN_CITYAREA_URL,
                      ADMIN_SUBJECTAREA_URL,
                      ADMIN_GLOBAL_CONTENT_URL,
                      ADMIN_HEARINGTYPE_URL,
                      ADMIN_KLE_URL,
                      ADMIN_FIELDTEMPLATES_URL,
                      ADMIN_INVITATIONGROUPS_URL,
                    ].includes(pathname),
                    iconProps: {
                      iconName: 'Settings',
                      className: 'left-side-menu-icon',
                    },
                    links: [
                      {
                        key: 'changeHearingOwner',
                        name: translator(
                          'pageNames.administrationAdministrateHearings',
                        ),
                        url: ADMIN_HOME_URL,
                      },
                      {
                        key: 'manageSubjectAreas',
                        name: translator(
                          'pageNames.administrationSubjectAreas',
                        ),
                        url: ADMIN_SUBJECTAREA_URL,
                      },
                      {
                        key: 'manageCityAreas',
                        name: translator('pageNames.administrationCityAreas'),
                        url: ADMIN_CITYAREA_URL,
                      },
                      {
                        key: 'manageGlobalContent',
                        name: translator(
                          'pageNames.administrationGlobalContents',
                        ),
                        url: ADMIN_GLOBAL_CONTENT_URL,
                      },
                      {
                        key: 'manageHearingTypes',
                        name: translator(
                          'pageNames.administrationHearingTypes',
                        ),
                        url: ADMIN_HEARINGTYPE_URL,
                      },
                      {
                        key: 'manageKle',
                        name: translator('pageNames.adminKleHierarchies'),
                        url: ADMIN_KLE_URL,
                      },
                      {
                        key: 'manageFieldTemplates',
                        name: translator('pageNames.adminFieldTemplates'),
                        url: ADMIN_FIELDTEMPLATES_URL,
                      },
                      {
                        key: 'manageInvitationGroups',
                        name: translator('pageNames.adminInvitationGroups'),
                        url: ADMIN_INVITATIONGROUPS_URL,
                      },
                    ],
                  },
                ]
              : []),
          ],
        },
      ]}
    />
  );
};

export { LeftSideMenu };
